<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import CopyButton from '@/components/aml-review/CopyButton.vue';
import TimestampWithIso from '@/components/ui/TimestampWithIso.vue';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import { AccountActions, FundingSourceActions } from '@/integrations/Immersve/auth/actions';
import type { CardDetail } from '@/integrations/Immersve/cards/types';
import { useAuthStore } from '@/stores/auth.store';
import {
  BLOCKED_TONE_CLASSES,
  STATUS_TONE_CLASSES,
  displayStatus,
  dashedLabel,
  expiryLabel,
  maskedPan,
  seriesOf,
} from './cardDisplay';

const props = defineProps<{ cardId: string }>();

const api = useImmersveApiClient();
const authStore = useAuthStore();
const card = ref<CardDetail | null>(null);
const loading = ref(true);
const error = ref<Error | null>(null);

async function load() {
  loading.value = true;
  error.value = null;
  card.value = null;
  try {
    card.value = await api.getCard(props.cardId);
  } catch (err) {
    error.value = err instanceof Error ? err : new Error(String(err));
  } finally {
    loading.value = false;
  }
}

watch(() => props.cardId, load, { immediate: true });

// The series page lists the account's cards, which takes account:read.
const canReadAccount = computed(
  () =>
    !!card.value &&
    (authStore.user?.isAuthorised(AccountActions.Read, `account:${card.value.accountId}`) ?? false)
);
const canReadFundingSources = computed(
  () =>
    !!card.value &&
    (authStore.user?.isAuthorised(FundingSourceActions.Read, `account:${card.value.accountId}`) ??
      false)
);
</script>

<template>
  <div>
    <div
      v-if="loading"
      data-test="card-loading"
      class="flex items-center gap-2 py-6 text-sm text-gray-500"
    >
      <ArrowPathIcon class="h-4 w-4 animate-spin" />
      Loading…
    </div>
    <p v-else-if="error" data-test="card-error" class="py-6 text-sm text-red-600">
      {{ error.message }}
    </p>
    <template v-else-if="card">
      <!-- Where the card sits: its account, which links back to the account's
      cards, and its own ID. -->
      <dl class="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm/6">
        <div class="flex min-w-0 items-center gap-x-2">
          <dt class="text-gray-500">Account</dt>
          <dd class="flex min-w-0 items-center gap-x-1.5">
            <RouterLink
              :to="`/cards?account=${encodeURIComponent(card.accountId)}`"
              class="truncate font-mono font-medium text-indigo-600 hover:text-indigo-700"
              data-test="card-account-link"
            >
              {{ card.accountId }}
            </RouterLink>
            <CopyButton :value="card.accountId" label="account ID" />
          </dd>
        </div>
        <div class="flex min-w-0 items-center gap-x-2">
          <dt class="text-gray-500">Card</dt>
          <dd class="flex min-w-0 items-center gap-x-1.5">
            <span class="truncate font-mono text-gray-900">{{ card.id }}</span>
            <CopyButton :value="card.id" label="card ID" />
          </dd>
        </div>
      </dl>

      <!-- Grouped by scope: the card itself, with when it was made and ended;
      beside it, what it draws on and what replaced it. -->
      <div class="mt-3 grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <section
          class="rounded-lg bg-white shadow-sm ring-1 ring-gray-200 lg:col-span-2"
          data-test="card-section-card"
        >
          <h3 class="border-b border-gray-100 px-4 py-2.5 text-sm/6 font-semibold text-gray-900">
            Card
          </h3>
          <dl class="divide-y divide-gray-100 px-4 text-sm/6">
            <div class="grid grid-cols-2 gap-x-4 py-2.5">
              <div>
                <dt class="text-gray-500">PAN</dt>
                <dd class="mt-0.5 text-gray-900" data-test="card-pan">{{ maskedPan(card) }}</dd>
              </div>
              <div>
                <dt class="text-gray-500">Status</dt>
                <dd class="mt-0.5 flex flex-wrap items-center gap-1.5">
                  <span
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset"
                    :class="STATUS_TONE_CLASSES[displayStatus(card)]"
                    data-test="card-status"
                  >
                    {{ displayStatus(card) }}
                  </span>
                  <span
                    v-if="card.isBlocked"
                    class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset"
                    :class="BLOCKED_TONE_CLASSES"
                    data-test="card-blocked"
                  >
                    Frozen{{ card.unfreezeAllowed === false ? ' · unfreeze not allowed' : '' }}
                  </span>
                </dd>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-x-4 py-2.5">
              <div>
                <dt class="text-gray-500">Expires</dt>
                <dd class="mt-0.5 text-gray-900 tabular-nums" data-test="card-expiry">
                  {{ expiryLabel(card) }}
                </dd>
              </div>
              <div>
                <dt class="text-gray-500">Type</dt>
                <dd class="mt-0.5 text-gray-900 capitalize" data-test="card-type">
                  {{ card.type }} · {{ card.network }}
                </dd>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-x-4 py-2.5">
              <div>
                <dt class="text-gray-500">Currency</dt>
                <dd class="mt-0.5 text-gray-900">{{ card.currency ?? '—' }}</dd>
              </div>
              <div>
                <dt class="text-gray-500">Region</dt>
                <dd class="mt-0.5 text-gray-900">{{ card.regionCode ?? '—' }}</dd>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-x-4 gap-y-2.5 py-2.5 sm:grid-cols-2">
              <div class="min-w-0">
                <dt class="text-gray-500">Card program</dt>
                <dd class="mt-0.5 truncate font-mono text-gray-900">
                  {{ card.cardProgramId ?? '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-gray-500">Cancel reason</dt>
                <dd
                  class="mt-0.5 text-gray-900 first-letter:uppercase"
                  data-test="card-cancel-reason"
                >
                  {{ dashedLabel(card.cancelReason) }}
                </dd>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-x-4 gap-y-2.5 py-2.5 sm:grid-cols-2">
              <div class="min-w-0">
                <dt class="text-gray-500">Created</dt>
                <dd class="mt-0.5"><TimestampWithIso :value="card.createdAt" /></dd>
              </div>
              <div class="min-w-0">
                <dt class="text-gray-500">Modified</dt>
                <dd class="mt-0.5"><TimestampWithIso :value="card.modifiedAt" /></dd>
              </div>
            </div>
          </dl>
          <div v-if="card.trackingUrl" class="border-t border-gray-100 px-4 py-2.5">
            <a
              :href="card.trackingUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm/6 font-semibold text-indigo-600 hover:text-indigo-700"
              data-test="card-tracking-link"
            >
              Track shipment <span aria-hidden="true">&rarr;</span>
            </a>
          </div>
        </section>

        <section
          class="rounded-lg bg-white shadow-sm ring-1 ring-gray-200"
          data-test="card-section-funding-reissue"
        >
          <h3 class="border-b border-gray-100 px-4 py-2.5 text-sm/6 font-semibold text-gray-900">
            Funding &amp; reissue
          </h3>
          <dl class="divide-y divide-gray-100 px-4 text-sm/6">
            <div class="min-w-0 py-2.5">
              <dt class="text-gray-500">Funding sources</dt>
              <dd class="mt-0.5 font-mono text-gray-900" data-test="card-funding-sources">
                <span v-if="card.fundingSourceIds.length === 0">—</span>
                <template v-for="id in card.fundingSourceIds" :key="id">
                  <RouterLink
                    v-if="canReadFundingSources"
                    :to="`/funding-sources/${encodeURIComponent(id)}`"
                    class="block truncate font-medium text-indigo-600 hover:text-indigo-700"
                    data-test="card-funding-source-link"
                  >
                    {{ id }}
                  </RouterLink>
                  <span v-else class="block truncate">{{ id }}</span>
                </template>
              </dd>
            </div>
            <div class="min-w-0 py-2.5">
              <dt class="text-gray-500">Reissue</dt>
              <dd class="mt-0.5 text-gray-900">
                <span class="block first-letter:uppercase">
                  {{ dashedLabel(card.reissuanceType) }}
                </span>
                <span class="mt-0.5 flex flex-wrap gap-x-4 gap-y-1">
                  <RouterLink
                    v-if="card.successorCardId"
                    :to="`/cards/${encodeURIComponent(card.successorCardId)}`"
                    class="min-w-0 truncate font-medium text-indigo-600 hover:text-indigo-700"
                    data-test="card-successor-link"
                  >
                    Successor <span class="font-mono">{{ card.successorCardId }}</span>
                  </RouterLink>
                  <RouterLink
                    v-if="canReadAccount"
                    :to="{
                      path: '/card-series',
                      query: { account: card.accountId, series: seriesOf(card) },
                    }"
                    class="font-medium text-indigo-600 hover:text-indigo-700"
                    data-test="card-series-link"
                  >
                    View series <span aria-hidden="true">&rarr;</span>
                  </RouterLink>
                </span>
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </template>
  </div>
</template>
